import { type ReactNode, useState, useCallback, useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import { Plus, GripVertical, Trash2, X, Filter, ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { reorderRfiRows } from './reorderRfiRows';
import { RfiFileDownload } from './RfiFileDownload';
import { useRfiAutosave, type RfiSaveState } from './useRfiAutosave';
import { BackendError } from '@/lib/backend';
import { listRfiRequests, syncRfiRequests } from '@/lib/api/rfi';
import type { RFIRequestFile, RFIRequestListItem, RFIRequestSyncItem, RfiPriority, RfiRequestType, RfiStatus } from '@/types/backend';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface RFIGridItem {
  id: string;
  reference: string;
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  status: 'Open' | 'In Progress' | 'Received' | 'Provided' | 'Closed' | 'Follow-up Required';
  date: string;
  area: string;
  documentRef: string;
  files: RFIRequestFile[];
  request: string;
  advisorComment: string;
  // Not currently rendered in the grid UI, but preserved for backend sync (avoid wiping existing values).
  managementComment?: string | null;
  section: string;
  /** Marks items added post-initial analysis */
  isNew?: boolean;
}

const SECTIONS = [
  { id: '1', name: 'Business context' },
  { id: '2', name: 'Historical trading' },
  { id: '3', name: 'Historical balance sheet and cash flows' },
  { id: '4', name: 'Forecast trading' },
];

type RfiGridColumnKey =
  | "ref"
  | "priority"
  | "status"
  | "date"
  | "area"
  | "docRef"
  | "request"
  | "managementNotes"
  | "advisorNotes";

// Store only manual overrides so future default-width changes take effect.
const COL_WIDTHS_STORAGE_KEY = "rfiGrid.colWidthOverrides.v2";

// Auto-resizing textarea component
interface AutoResizeTextareaProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  className?: string;
  readOnly?: boolean;
}

function AutoResizeTextarea({ value, onChange, placeholder, className, readOnly }: AutoResizeTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    // Set height to scrollHeight to fit content (min 36px)
    const newHeight = Math.max(36, textarea.scrollHeight);
    textarea.style.height = `${newHeight}px`;
  }, []);
  
  // Adjust height when value changes
  useLayoutEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);
  
  // Adjust height when container is resized (e.g., column resize)
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const resizeObserver = new ResizeObserver(() => {
      adjustHeight();
    });
    
    resizeObserver.observe(textarea);
    
    return () => {
      resizeObserver.disconnect();
    };
  }, [adjustHeight]);
  
  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={1}
      readOnly={readOnly}
      className={className}
      style={{ overflow: 'hidden' }}
    />
  );
}

const DEFAULT_TEXT_COLUMN_WIDTH = 190;
const RFI_CELL_TEXT_CLASS = "text-rfi-body font-sans font-normal text-foreground";

const DEFAULT_COL_WIDTHS: Record<RfiGridColumnKey, number> = {
  ref: 44,
  priority: 68,
  status: 88,
  date: 64,
  area: 100,
  docRef: 70,
  request: DEFAULT_TEXT_COLUMN_WIDTH,
  managementNotes: DEFAULT_TEXT_COLUMN_WIDTH,
  advisorNotes: DEFAULT_TEXT_COLUMN_WIDTH,
};

export interface ReconciliationFinding {
  areaKeywords: string[]; // keywords to match against item area/request
  note: string;
}

interface RFIGridProps {
  projectId?: string;
  filterStatus?: "All" | RFIGridItem["status"];
  filterPriority?: "All" | RFIGridItem["priority"];
  filterCategory?: "All" | string; // matches section.name (e.g. "Business context")
  excludeStatuses?: RFIGridItem["status"][]; // hide items with these statuses
  reconciliationFindings?: ReconciliationFinding[]; // inject findings into advisor notes
  showStatusToolbar?: boolean;
  toolbarActions?: ReactNode;
  showManagementNotes?: boolean; // show Management Notes column (default: false)
  onExposeSave?: (save: RfiSaveState) => void;
  onExposeStats?: (stats: { open: number; inProgress: number; received: number }) => void;
  /** When false, all editing controls are hidden/disabled. Defaults to true. */
  canEdit?: boolean;
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function formatDateToDmy(date: Date): string {
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${String(date.getFullYear()).slice(-2)}`;
}

function todayDmyDate(): string {
  return formatDateToDmy(new Date());
}

function isValidDmyDate(value: string): boolean {
  const v = value.trim();
  const m = /^(\d{2})\/(\d{2})\/(\d{2})$/.exec(v);
  if (!m) return false;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yy = Number(m[3]);
  if (!Number.isFinite(dd) || !Number.isFinite(mm) || !Number.isFinite(yy)) return false;
  if (mm < 1 || mm > 12) return false;
  if (dd < 1 || dd > 31) return false;

  // Convert 2-digit year to 4-digit (assume 2000s for 00-99)
  const yyyy = 2000 + yy;
  const daysInMonth = new Date(Date.UTC(yyyy, mm, 0)).getUTCDate(); // mm is 1..12
  return dd <= daysInMonth;
}

function normalizeDmyInput(value: string): string {
  // Keep only digits, then insert slashes as DD/MM/YY.
  const digits = value.replace(/[^\d]/g, "").slice(0, 6); // DDMMYY
  const dd = digits.slice(0, 2);
  const mm = digits.slice(2, 4);
  const yy = digits.slice(4, 6);
  if (digits.length <= 2) return dd;
  if (digits.length <= 4) return `${dd}/${mm}`;
  return `${dd}/${mm}/${yy}`;
}

export const RFIGrid = ({
  projectId,
  filterStatus = "All",
  filterPriority = "All",
  filterCategory = "All",
  excludeStatuses,
  reconciliationFindings,
  showStatusToolbar = false,
  toolbarActions,
  showManagementNotes = false,
  onExposeSave,
  onExposeStats,
  canEdit = true,
}: RFIGridProps) => {
  const { items, editItems, resetItems, handleSave: handleSaveChanges, hasUnsavedChanges, status: saveStatus } = useRfiAutosave<RFIGridItem>({
    enabled: Boolean(projectId) && canEdit,
    save: persistItems,
    onError: (error) => {
      toast({
        title: "Changes could not be saved",
        description: error instanceof Error ? error.message : "Please retry when your connection is restored.",
        variant: "destructive",
      });
    },
  });
  const [draggedRow, setDraggedRow] = useState<{ id: string; section: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: string; placement: 'before' | 'after' } | null>(null);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  // Column-based filtering
  const [internalFilterStatus, setInternalFilterStatus] = useState<"All" | "New" | RFIGridItem["status"]>("All");
  const [internalFilterPriority, setInternalFilterPriority] = useState<"All" | RFIGridItem["priority"]>("All");
  const [filterTextRef, setFilterTextRef] = useState("");
  const [filterTextArea, setFilterTextArea] = useState("");
  const [filterTextRequest, setFilterTextRequest] = useState("");
  const [filterTextDate, setFilterTextDate] = useState("");
  const [filterTextAdvisor, setFilterTextAdvisor] = useState("");
  const [filterTextManagement, setFilterTextManagement] = useState("");
  const [colWidthOverrides, setColWidthOverrides] = useState<Partial<Record<RfiGridColumnKey, number>>>(() => {
    try {
      const raw = localStorage.getItem(COL_WIDTHS_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as Partial<Record<RfiGridColumnKey, number>>;
      return parsed;
    } catch {
      return {};
    }
  });
  const colWidths = useMemo(() => ({ ...DEFAULT_COL_WIDTHS, ...colWidthOverrides }), [colWidthOverrides]);
  const tableMinWidth = 32 + 40 + colWidths.ref + colWidths.priority + colWidths.status
    + colWidths.date + colWidths.area + colWidths.request + colWidths.docRef + colWidths.advisorNotes
    + (showManagementNotes ? colWidths.managementNotes : 0);
  const columnWidth = (key: RfiGridColumnKey): number | string => {
    // Only text columns share extra space; metadata widths stay in actual pixels.
    if (key === "request" || key === "advisorNotes" || key === "managementNotes") {
      return `calc(${colWidths[key]}px + (100% - ${tableMinWidth}px) / ${showManagementNotes ? 3 : 2})`;
    }
    return colWidths[key];
  };
  const activeColResizeRef = useRef<{
    key: RfiGridColumnKey;
    startX: number;
    startWidth: number;
  } | null>(null);
  const sectionNameById = useCallback((id: string): string => {
    return SECTIONS.find((s) => s.id === id)?.name ?? id;
  }, []);

  const isItemVisible = useCallback((it: RFIGridItem): boolean => {
    // Exclude items with certain statuses
    if (excludeStatuses && excludeStatuses.includes(it.status)) return false;
    // Use internal filters if set, otherwise fallback to external filters
    const activeStatusFilter = internalFilterStatus !== "All" ? internalFilterStatus : filterStatus;
    const activePriorityFilter = internalFilterPriority !== "All" ? internalFilterPriority : filterPriority;
    
    if (activeStatusFilter === "New") {
      if (!it.isNew) return false;
    } else if (activeStatusFilter !== "All" && it.status !== activeStatusFilter) return false;
    if (activePriorityFilter !== "All" && it.priority !== activePriorityFilter) return false;
    if (filterCategory !== "All" && sectionNameById(it.section) !== filterCategory) return false;
    // Text filters
    if (filterTextRef && !it.reference.toLowerCase().includes(filterTextRef.toLowerCase())) return false;
    if (filterTextArea && !it.area.toLowerCase().includes(filterTextArea.toLowerCase())) return false;
    if (filterTextRequest && !it.request.toLowerCase().includes(filterTextRequest.toLowerCase())) return false;
    if (filterTextDate && !it.date.toLowerCase().includes(filterTextDate.toLowerCase())) return false;
    if (filterTextAdvisor && !it.advisorComment.toLowerCase().includes(filterTextAdvisor.toLowerCase())) return false;
    if (filterTextManagement && !(it.managementComment ?? "").toLowerCase().includes(filterTextManagement.toLowerCase())) return false;
    return true;
  }, [excludeStatuses, internalFilterStatus, internalFilterPriority, filterStatus, filterPriority, filterCategory, sectionNameById, filterTextRef, filterTextArea, filterTextRequest, filterTextDate, filterTextAdvisor, filterTextManagement]);

  const visibleItems = useMemo(() => items.filter(isItemVisible), [items, isItemVisible]);

  // Expose stats to parent
  useEffect(() => {
    if (onExposeStats) {
      const stats = {
        open: items.filter(it => it.status === 'Open').length,
        inProgress: items.filter(it => it.status === 'In Progress').length,
        received: items.filter(it => it.status === 'Received').length,
      };
      onExposeStats(stats);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    try {
      localStorage.setItem(COL_WIDTHS_STORAGE_KEY, JSON.stringify(colWidthOverrides));
    } catch {
      // ignore
    }
  }, [colWidthOverrides]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const active = activeColResizeRef.current;
      if (!active) return;
      const dx = e.clientX - active.startX;
      const next = Math.max(Math.min(80, DEFAULT_COL_WIDTHS[active.key]), active.startWidth + dx);
      setColWidthOverrides((prev) => ({ ...prev, [active.key]: next }));
    };
    const onUp = () => {
      activeColResizeRef.current = null;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const startColResize = useCallback((key: RfiGridColumnKey, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    activeColResizeRef.current = {
      key,
      startX: e.clientX,
      startWidth: colWidths[key],
    };
  }, [colWidths]);
  
  // Auto-fit column on double-click
  const autoFitColumn = useCallback((key: RfiGridColumnKey) => {
    // Calculate the maximum content width for this column
    // This is a simplified auto-fit that sets a reasonable width based on content
    const contentWidths: Record<RfiGridColumnKey, number> = {
      ref: 60,
      priority: 90,
      status: 110,
      date: 85,
      area: 140,
      docRef: 90,
      request: 250,
      managementNotes: 250,
      advisorNotes: 250,
    };
    
    setColWidthOverrides(prev => ({ ...prev, [key]: contentWidths[key] }));
  }, []);

  // Load items from backend (source of truth). No local mocks.
  useEffect(() => {
    if (!projectId) return;

    let cancelled = false;
    setIsLoadingItems(true);
    (async () => {
      try {
        const rows = await listRfiRequests(projectId);
        if (cancelled) return;
        const mapped: RFIGridItem[] = rows.map((r) => ({
          id: String(r.id),
          reference: r.ref,
          priority: fromBackendPriority(r.priority),
          status: fromBackendStatus(r.status),
          date: import.meta.env?.VITE_DEMO_MODE === "true" ? todayDmyDate() : normalizeBackendDate(r.date),
          area: r.area,
          documentRef: r.document_ref,
          files: r.files,
          request: r.requests,
          advisorComment: r.advisor_notes ?? "",
          managementComment: r.management_comment ?? undefined,
          section: fromBackendSection(r.type),
        }));
        // Inject reconciliation findings into advisor notes
        let finalMapped = mapped;
        if (reconciliationFindings && reconciliationFindings.length > 0) {
          finalMapped = mapped.map((item) => {
            const matchingFindings = reconciliationFindings.filter((f) =>
              f.areaKeywords.some((kw) => {
                const kwLower = kw.toLowerCase();
                return item.area.toLowerCase().includes(kwLower) || item.request.toLowerCase().includes(kwLower);
              })
            );
            if (matchingFindings.length > 0) {
              const existingNote = item.advisorComment ? item.advisorComment.trim() : '';
              const newNotes = matchingFindings.map((f) => f.note).join('\n');
              return {
                ...item,
                advisorComment: existingNote ? `${existingNote}\n${newNotes}` : newNotes,
              };
            }
            return item;
          });
        }
        resetItems(finalMapped);
      } catch (e) {
        if (cancelled) return;
        const message =
          e instanceof BackendError
            ? `${e.message} (HTTP ${e.status})`
            : e instanceof Error
              ? e.message
              : "Unknown error";
        toast({
          title: "Failed to load RFI requests",
          description: message,
          variant: "destructive",
        });
        resetItems([]);
      } finally {
        if (cancelled) return;
        setIsLoadingItems(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, resetItems, reconciliationFindings]);

  function toBackendPriority(p: RFIGridItem["priority"]): RfiPriority {
    switch (p) {
      case "Critical":
        return "critical";
      case "High":
        return "high";
      case "Medium":
        return "medium";
      case "Low":
        return "low";
    }
  }

  function toBackendStatus(s: RFIGridItem["status"]): RfiStatus {
    switch (s) {
      case "Open":
        return "open";
      case "In Progress":
      case "Follow-up Required":
        return "in_progress";
      case "Received":
      case "Provided":
      case "Closed":
        return "closed";
    }
  }

  function toBackendType(section: string): RfiRequestType {
    switch (section) {
      case "1":
        return "business_context";
      case "2":
        return "historical_trading";
      case "3":
        return "historical_balance_sheet_and_cash_flows";
      case "4":
        return "forecast_trading";
      default:
        return "business_context";
    }
  }

  function fromBackendPriority(p: RFIRequestListItem["priority"]): RFIGridItem["priority"] {
    switch (p) {
      case "critical":
        return "Critical";
      case "high":
        return "High";
      case "medium":
        return "Medium";
      case "low":
        return "Low";
    }
  }

  function fromBackendStatus(s: RFIRequestListItem["status"]): RFIGridItem["status"] {
    switch (s) {
      case "open":
        return "Open";
      case "in_progress":
        return "In Progress";
      case "closed":
        return "Received";
    }
  }

  function fromBackendSection(t: RFIRequestListItem["type"]): string {
    switch (t) {
      case "business_context":
        return "1";
      case "historical_trading":
        return "2";
      case "historical_balance_sheet_and_cash_flows":
        return "3";
      case "forecast_trading":
        return "4";
    }
  }

  function normalizeBackendDate(value: string | null | undefined): string {
    if (!value) return "";
    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      const yy = value.slice(2, 4); // 2-digit year
      const mm = value.slice(5, 7);
      const dd = value.slice(8, 10);
      return `${dd}/${mm}/${yy}`;
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return formatDateToDmy(d);
  }

  function toBackendDate(value: string): string | null {
    const v = value.trim();
    if (!v) return null;

    // Accept date-time strings, but send date only.
    if (v.includes("T") && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 10);

    // YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;

    // DD/MM/YY -> convert (assume 2000s).
    const m = /^(\d{2})\/(\d{2})\/(\d{2})$/.exec(v);
    if (m) {
      const [, dd, mm, yy] = m;
      return `20${yy}-${mm}-${dd}`;
    }

    // Fallback: send as-is (backend will validate).
    return v;
  }

  async function persistItems(snapshot: RFIGridItem[]): Promise<RFIGridItem[]> {
    if (!projectId) throw new Error("No project selected.");

    // Backend-driven validation: let the backend validate and return errors.
    // New backend contract: send the FULL list (create/update/delete in one call).
    const payload: RFIRequestSyncItem[] = snapshot.map((it) => {
      const idNum = Number(it.id);
      const hasBackendId = Number.isFinite(idNum);

      const advisorNotes = (it.advisorComment ?? "").trim();
      const base: RFIRequestSyncItem = {
        id: hasBackendId ? idNum : null,
        type: toBackendType(it.section),
        ref: it.reference,
        priority: toBackendPriority(it.priority),
        status: toBackendStatus(it.status),
        date: toBackendDate(it.date),
        area: it.area,
        document_ref: it.documentRef,
        requests: it.request,
        advisor_notes: advisorNotes,
        management_comment: it.managementComment ?? null,
      };

      return base;
    });

    const rows = await syncRfiRequests(projectId, payload);
    return rows.map((r) => ({
      id: String(r.id),
      reference: r.ref,
      priority: fromBackendPriority(r.priority),
      status: fromBackendStatus(r.status),
      date: normalizeBackendDate(r.date),
      area: r.area,
      documentRef: r.document_ref,
      files: r.files,
      request: r.requests,
      advisorComment: r.advisor_notes ?? "",
      managementComment: r.management_comment ?? undefined,
      section: fromBackendSection(r.type),
    }));
  }

  // Expose save handler and state to parent
  useEffect(() => {
    if (onExposeSave) {
      onExposeSave({
        handleSave: handleSaveChanges,
        hasUnsavedChanges,
        status: saveStatus,
      });
    }
  }, [onExposeSave, handleSaveChanges, hasUnsavedChanges, saveStatus]);

  // Ctrl+S / Cmd+S to save
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (key !== "s") return;
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      void handleSaveChanges();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleSaveChanges]);

  const handleCellEdit = useCallback((id: string, field: keyof RFIGridItem, value: string) => {
    if (!canEdit) return;
    editItems(prev => prev.map(item =>
      item.id === id ? {
        ...item,
        [field]: value,
        files: (field === "request" || field === "area") && item[field] !== value ? [] : item.files,
      } : item
    ));
  }, [canEdit, editItems]);

  const handleDateChange = useCallback((id: string, raw: string) => {
    handleCellEdit(id, "date", normalizeDmyInput(raw));
  }, [handleCellEdit]);

  const handleDateBlur = useCallback((id: string) => {
    editItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const v = item.date.trim();
        if (!v) return item;
        // If it's not a valid DD/MM/YY, keep it (user may be mid-edit), but saving will be blocked.
        // If it is valid but partially entered (shouldn't happen with mask), normalize it.
        if (!isValidDmyDate(v)) return item;
        const norm = normalizeDmyInput(v);
        return norm === item.date ? item : { ...item, date: norm };
      })
    );
  }, [editItems]);

  const renumberItems = useCallback((itemsList: RFIGridItem[]): RFIGridItem[] => {
    const sectionCounters: Record<string, number> = {};
    return itemsList.map(item => {
      if (!sectionCounters[item.section]) {
        sectionCounters[item.section] = 1;
      }
      const newRef = `${item.section}.${String(sectionCounters[item.section]).padStart(2, '0')}`;
      sectionCounters[item.section]++;
      return { ...item, reference: newRef };
    });
  }, []);

  const handleInsertRowBelow = useCallback((afterId: string) => {
    if (!projectId || !canEdit) return;
    const newId = `new-${crypto.randomUUID()}`;
    const currentItem = items.find(item => item.id === afterId);
    const section = currentItem?.section || '1';
    
    editItems(prev => {
      const index = prev.findIndex(item => item.id === afterId);
      const newItem: RFIGridItem = {
        id: newId,
        reference: '',
        priority: 'Medium',
        status: 'Open',
        date: todayDmyDate(),
        area: '',
        documentRef: '[n/a]',
        files: [],
        request: '',
        advisorComment: '',
        managementComment: '',
        section: section,
        isNew: true,
      };
      const newItems = [...prev];
      newItems.splice(index + 1, 0, newItem);
      return renumberItems(newItems);
    });
  }, [items, projectId, renumberItems, canEdit, editItems]);

  const handleDeleteRow = useCallback((id: string) => {
    if (!projectId || !canEdit) return;
    editItems(prev => renumberItems(prev.filter(item => item.id !== id)));
  }, [projectId, renumberItems, canEdit, editItems]);

  const handleReorderRow = useCallback((id: string, targetId: string, placement: 'before' | 'after') => {
    if (!projectId || !canEdit) return;
    const reordered = reorderRfiRows(items, id, targetId, placement);
    if (reordered === items) return;
    editItems(() => renumberItems(reordered));
  }, [projectId, canEdit, items, renumberItems, editItems]);

  const clearRowDrag = () => {
    setDraggedRow(null);
    setDropTarget(null);
  };

  const priorityColors: Record<string, string> = {
    Critical: 'bg-red-800/90 text-white border-red-800',
    High: 'bg-destructive/10 text-destructive border-destructive/20',
    Medium: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    Low: 'bg-muted text-muted-foreground border-border',
  };

  const statusColors: Record<string, string> = {
    'Open': 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    'In Progress': 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    'Follow-up Required': 'bg-orange-500/10 text-orange-600 border-orange-500/20',
    'Received': 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
    'Provided': 'bg-violet-500/10 text-violet-600 border-violet-500/20',
  };

  const hasAnyFilter = internalFilterPriority !== "All" || internalFilterStatus !== "All" || filterTextRef || filterTextArea || filterTextRequest || filterTextDate || filterTextAdvisor || filterTextManagement;

  const FilterHeaderSelect = ({ label, value, options, onChange, colKey }: { label: string; value: string; options: string[]; onChange: (v: any) => void; colKey: RfiGridColumnKey }) => (
    <th
      className="relative px-1.5 py-3 text-center text-rfi-label font-semibold text-muted-foreground uppercase tracking-wider border-r border-border"
      style={{ width: columnWidth(colKey) }}
    >
      <Popover>
        <PopoverTrigger asChild>
          <button className="hover:text-foreground transition-colors cursor-pointer flex items-center gap-1 mx-auto" title="Click to filter">
            {label}
            {value !== "All" ? (
              <span className="text-[7px] px-1 py-0.5 rounded bg-primary/20 text-primary normal-case">{value}</span>
            ) : (
              <ChevronDown className="w-3 h-3 opacity-40" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-36 p-1 z-50 bg-popover" align="start" sideOffset={4}>
          {options.map(opt => (
            <button
              key={opt}
              onClick={() => onChange(opt)}
              className={`w-full text-left text-rfi-label px-2.5 py-1.5 rounded hover:bg-accent transition-colors ${value === opt ? 'bg-accent text-accent-foreground font-medium' : ''}`}
            >
              {opt}
            </button>
          ))}
        </PopoverContent>
      </Popover>
      <div
        role="separator"
        aria-orientation="vertical"
        title="Drag to resize, double-click to auto-fit"
        className="absolute right-0 top-0 h-full w-2 cursor-col-resize select-none hover:bg-muted after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-border hover:after:bg-accent"
        onMouseDown={(e) => startColResize(colKey, e)}
        onDoubleClick={() => autoFitColumn(colKey)}
      />
    </th>
  );

  const FilterHeaderText = ({ label, value, onChange, colKey, placeholder }: { label: string; value: string; onChange: (v: string) => void; colKey: RfiGridColumnKey; placeholder?: string }) => (
    <th
      className={`relative py-3 text-center text-rfi-label font-semibold text-muted-foreground uppercase tracking-wider border-r border-border ${colKey === "ref" || colKey === "date" ? "px-1.5" : "px-3"}`}
      style={{ width: columnWidth(colKey) }}
    >
      <Popover>
        <PopoverTrigger asChild>
          <button className="hover:text-foreground transition-colors cursor-pointer flex items-center gap-1 mx-auto" title="Click to filter">
            {label}
            {value ? (
              <span className="text-[7px] px-1 py-0.5 rounded bg-primary/20 text-primary normal-case truncate max-w-[60px]">{value}</span>
            ) : (
              <ChevronDown className="w-3 h-3 opacity-40" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-2 z-50 bg-popover" align="start" sideOffset={4}>
          <div className="flex items-center gap-1.5 border rounded px-2 py-1 bg-background">
            <Search className="w-3 h-3 text-muted-foreground shrink-0" />
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder || `Filter ${label.toLowerCase()}...`}
              className="w-full text-rfi-label bg-transparent outline-none placeholder:text-muted-foreground/50"
              autoFocus
            />
            {value && (
              <button onClick={() => onChange("")} className="text-muted-foreground hover:text-foreground">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>
      <div
        role="separator"
        aria-orientation="vertical"
        title="Drag to resize, double-click to auto-fit"
        className="absolute right-0 top-0 h-full w-2 cursor-col-resize select-none hover:bg-muted after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-border hover:after:bg-accent"
        onMouseDown={(e) => startColResize(colKey, e)}
        onDoubleClick={() => autoFitColumn(colKey)}
      />
    </th>
  );

  const statusFilters = [
    { value: "All", label: "Total", count: items.length },
    { value: "Open", label: "Open", count: items.filter(item => item.status === "Open").length, dot: "bg-muted-foreground/50" },
    { value: "In Progress", label: "In progress", count: items.filter(item => item.status === "In Progress").length, dot: "bg-muted-foreground/70" },
    // The grid maps the backend's closed status to Received.
    { value: "Received", label: "Closed", count: items.filter(item => item.status === "Received").length, dot: "bg-foreground" },
    { value: "New", label: "New", count: items.filter(item => item.isNew).length, dot: "bg-primary" },
  ] as const;

  return (
    <div className="animate-fade-in w-full">
      {showStatusToolbar && (
        <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-border bg-card px-3 py-2">
          <div role="group" aria-label="Filter RFI by status" className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {statusFilters.map(({ value, label, count, ...filter }) => (
              <div key={value} className="flex items-center gap-6">
                {value !== "All" && <div aria-hidden="true" className="h-4 w-px bg-border" />}
                <button
                  type="button"
                  aria-pressed={internalFilterStatus === value}
                  onClick={() => setInternalFilterStatus(current => current === value ? "All" : value)}
                  className={cn(
                    "flex items-center gap-2 rounded px-2 py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    internalFilterStatus === value ? (value === "New" ? "bg-primary/10" : "bg-muted") : "hover:bg-muted/50",
                  )}
                >
                  <span className={cn("text-sm font-semibold", value === "New" ? "text-primary" : "text-foreground")}>{count}</span>
                  {"dot" in filter && <span aria-hidden="true" className={cn("h-1.5 w-1.5 rounded-full", filter.dot)} />}
                  <span className={cn("text-xs whitespace-nowrap", value === "New" ? "font-medium text-primary" : "text-muted-foreground")}>{label}</span>
                </button>
              </div>
            ))}
          </div>
          <div className="ml-auto">{toolbarActions}</div>
        </div>
      )}
      {/* RFI Grid */}
      <Card variant="premium" className="border-0 shadow-none rounded-none">
        <CardContent className="p-0">
          <div className="max-h-[calc(100vh-100px)] overflow-y-auto">
            <table className="w-full table-fixed" style={{ tableLayout: 'fixed', minWidth: tableMinWidth }}>
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="w-8 px-2 py-3"></th>
                  <FilterHeaderText label="RFI ID" value={filterTextRef} onChange={setFilterTextRef} colKey="ref" placeholder="Search ID..." />
                  <FilterHeaderSelect label="Priority" value={internalFilterPriority} options={["All", "Critical", "High", "Medium", "Low"]} onChange={setInternalFilterPriority} colKey="priority" />
                  <FilterHeaderSelect label="Status" value={internalFilterStatus} options={["All", "Open", "In Progress", "Follow-up Required", "Received", "Provided"]} onChange={setInternalFilterStatus} colKey="status" />
                  <FilterHeaderText label="Date" value={filterTextDate} onChange={setFilterTextDate} colKey="date" placeholder="Search date..." />
                  <FilterHeaderText label="Section" value={filterTextArea} onChange={setFilterTextArea} colKey="area" placeholder="Search section..." />
                  <FilterHeaderText label="Request" value={filterTextRequest} onChange={setFilterTextRequest} colKey="request" placeholder="Search request..." />
                  <th className="px-2 py-3 text-center text-rfi-label font-semibold text-muted-foreground uppercase tracking-wider border-r border-border" style={{ width: colWidths.docRef }}>File</th>
                  {showManagementNotes && (
                    <FilterHeaderText label="Management Notes" value={filterTextManagement} onChange={setFilterTextManagement} colKey="managementNotes" placeholder="Search notes..." />
                  )}
                  <FilterHeaderText label="FDD Comment" value={filterTextAdvisor} onChange={setFilterTextAdvisor} colKey="advisorNotes" placeholder="Search comments..." />
                  <th className="w-10 px-2 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {isLoadingItems && items.length === 0 && (
                  <tr className="border-b border-border">
                    <td colSpan={showManagementNotes ? 11 : 10} className="px-3 py-6 text-center text-rfi-body text-muted-foreground">
                      Loading RFI requests...
                    </td>
                  </tr>
                )}
                {SECTIONS.map((section) => {
                  const sectionItemsAll = items.filter(item => item.section === section.id);
                  const sectionItems = sectionItemsAll.filter(isItemVisible);
                  if (sectionItems.length === 0) return null;
                  
                  return (
                    <>
                      {/* Section Header */}
                      <tr key={`section-${section.id}`} className="bg-primary/90">
                        <td colSpan={showManagementNotes ? 11 : 10} className="px-3 py-2">
                          <span className="text-rfi-body font-semibold text-primary-foreground">
                            {section.id}. {section.name}
                          </span>
                        </td>
                      </tr>
                      {/* Section Items */}
                      {sectionItems.map((item, index) => (
                          <tr 
                            key={item.id} 
                            className="border-b border-border hover:bg-muted/50 transition-colors group align-top"
                            style={{
                              opacity: draggedRow?.id === item.id ? 0.4 : undefined,
                              boxShadow: dropTarget?.id === item.id
                                ? `inset 0 ${dropTarget.placement === 'before' ? 2 : -2}px 0 hsl(var(--primary))`
                                : undefined,
                            }}
                            onDragOver={(event) => {
                              if (!canEdit || !projectId || !draggedRow || draggedRow.id === item.id || draggedRow.section !== item.section) return;
                              event.preventDefault();
                              event.dataTransfer.dropEffect = 'move';
                              const bounds = event.currentTarget.getBoundingClientRect();
                              const placement = event.clientY < bounds.top + bounds.height / 2 ? 'before' : 'after';
                              setDropTarget(current => current?.id === item.id && current.placement === placement ? current : { id: item.id, placement });
                            }}
                            onDragLeave={(event) => {
                              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropTarget(null);
                            }}
                            onDrop={(event) => {
                              if (!draggedRow) return;
                              event.preventDefault();
                              const bounds = event.currentTarget.getBoundingClientRect();
                              handleReorderRow(draggedRow.id, item.id, event.clientY < bounds.top + bounds.height / 2 ? 'before' : 'after');
                              clearRowDrag();
                            }}
                          >
                            <td className="px-2 py-2">
                              {canEdit && projectId && (
                                <button
                                  type="button"
                                  draggable
                                  aria-label={`Reorder RFI ${item.reference}`}
                                  title="Drag to reorder within this section, or use the Up and Down arrow keys"
                                  className="p-0.5 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground opacity-40 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-1 transition-opacity"
                                  onDragStart={(event) => {
                                    setDraggedRow({ id: item.id, section: item.section });
                                    event.dataTransfer.effectAllowed = 'move';
                                    event.dataTransfer.setData('text/plain', item.id);
                                    event.dataTransfer.setDragImage(event.currentTarget.closest('tr')!, 16, 16);
                                  }}
                                  onDragEnd={clearRowDrag}
                                  onKeyDown={(event) => {
                                    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
                                    event.preventDefault();
                                    const target = sectionItems[index + (event.key === 'ArrowUp' ? -1 : 1)];
                                    if (target) handleReorderRow(item.id, target.id, event.key === 'ArrowUp' ? 'before' : 'after');
                                  }}
                                >
                                  <GripVertical className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </td>
                            <td className="px-1.5 py-2">
                              <div className="flex items-center gap-1.5">
                                <input
                                  type="text"
                                  value={item.reference}
                                  readOnly={!canEdit}
                                  onChange={(e) => handleCellEdit(item.id, 'reference', e.target.value)}
                                  className={`w-full bg-transparent border-0 ${RFI_CELL_TEXT_CLASS} focus:outline-none focus:ring-1 focus:ring-accent/50 rounded px-1 py-0.5 -mx-1`}
                                />
                                {item.isNew && (
                                  <span className="shrink-0 text-[6.3px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded tracking-wide uppercase">New</span>
                                )}
                              </div>
                            </td>
                            <td className="px-1 py-2">
                              <select
                                value={item.priority}
                                disabled={!canEdit}
                                onChange={(e) => handleCellEdit(item.id, 'priority', e.target.value)}
                                className={`text-rfi-label font-medium px-0.5 py-1 rounded border cursor-pointer focus:outline-none focus:ring-1 focus:ring-accent/50 w-full disabled:cursor-not-allowed disabled:opacity-70 ${priorityColors[item.priority]}`}
                              >
                                <option value="Critical">Critical</option>
                                <option value="High">High</option>
                                <option value="Medium">Medium</option>
                                <option value="Low">Low</option>
                              </select>
                            </td>
                            <td className="px-1 py-2">
                              <select
                                value={item.status}
                                disabled={!canEdit}
                                onChange={(e) => handleCellEdit(item.id, 'status', e.target.value)}
                                className={`text-rfi-label font-medium px-0.5 py-1 rounded border cursor-pointer focus:outline-none focus:ring-1 focus:ring-accent/50 w-full disabled:cursor-not-allowed disabled:opacity-70 ${statusColors[item.status] || ''}`}
                              >
                                <option value="Open">Open</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Follow-up Required">Follow-up Required</option>
                                <option value="Received">Received</option>
                                <option value="Provided">Provided</option>
                              </select>
                            </td>
                            <td className="px-1.5 py-2">
                              <input
                                type="text"
                                inputMode="numeric"
                                placeholder="DD/MM/YY"
                                value={item.date}
                                readOnly={!canEdit}
                                onChange={(e) => handleDateChange(item.id, e.target.value)}
                                onBlur={() => handleDateBlur(item.id)}
                                className={`w-full bg-transparent border-0 ${RFI_CELL_TEXT_CLASS} focus:outline-none focus:ring-1 focus:ring-accent/50 rounded px-1 py-0.5 -mx-1`}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <AutoResizeTextarea
                                value={item.area}
                                readOnly={!canEdit}
                                onChange={(e) => handleCellEdit(item.id, 'area', e.target.value)}
                                placeholder="Area"
                                className={`block w-full bg-transparent border-0 ${RFI_CELL_TEXT_CLASS} focus:outline-none focus:ring-1 focus:ring-accent/50 rounded placeholder:text-muted-foreground/50 resize-none px-1 py-0.5 -mx-1 whitespace-pre-wrap break-words`}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <AutoResizeTextarea
                                value={item.request}
                                readOnly={!canEdit}
                                onChange={(e) => handleCellEdit(item.id, 'request', e.target.value)}
                                placeholder="Enter request..."
                                className={`block w-full bg-transparent border-0 ${RFI_CELL_TEXT_CLASS} focus:outline-none focus:ring-1 focus:ring-accent/50 rounded placeholder:text-muted-foreground/50 resize-none px-1 py-0.5 -mx-1 whitespace-pre-wrap break-words`}
                              />
                            </td>
                            <td className="px-2 py-2 text-center">
                              {projectId
                                ? <RfiFileDownload projectId={projectId} requestId={item.id} files={item.files} />
                                : <span className="text-muted-foreground">—</span>}
                            </td>
                            {showManagementNotes && (
                              <td className="px-3 py-2">
                                <AutoResizeTextarea
                                  value={item.managementComment ?? ''}
                                  readOnly={!canEdit}
                                  onChange={(e) => handleCellEdit(item.id, 'managementComment', e.target.value)}
                                  placeholder="Add management notes..."
                                  className={`block w-full bg-transparent border-0 ${RFI_CELL_TEXT_CLASS} focus:outline-none focus:ring-1 focus:ring-accent/50 rounded placeholder:text-muted-foreground/50 resize-none px-1 py-0.5 -mx-1 whitespace-pre-wrap break-words`}
                                />
                              </td>
                            )}
                            <td className="px-3 py-2">
                              <AutoResizeTextarea
                                value={item.advisorComment}
                                readOnly={!canEdit}
                                onChange={(e) => handleCellEdit(item.id, 'advisorComment', e.target.value)}
                                placeholder="Add FDD comment..."
                                className={`block w-full bg-transparent border-0 ${RFI_CELL_TEXT_CLASS} focus:outline-none focus:ring-1 focus:ring-accent/50 rounded placeholder:text-muted-foreground/50 resize-none px-1 py-0.5 -mx-1 whitespace-pre-wrap break-words`}
                              />
                            </td>
                            <td className="px-2 py-2">
                              {canEdit && (
                                <div className="flex items-center gap-0.5">
                                  <button
                                    onClick={() => handleInsertRowBelow(item.id)}
                                    className="p-1.5 text-muted-foreground hover:text-accent opacity-0 group-hover:opacity-100 transition-all"
                                    title="Add row below"
                                  >
                                    <Plus className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteRow(item.id)}
                                    className="p-1.5 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-all"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                      ))}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {visibleItems.length === 0 && (
            <div className="py-12 text-center text-muted-foreground">
              <p className="text-sm">
                {items.length === 0
                  ? "No items yet. Use the + button on any row to add one below."
                  : "No items match the current filters."}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
